import React, { useState, useEffect, useRef } from 'react';

const Customers = () => {
  const [activeReview, setActiveReview] = useState(0);
  const [hoveredCustomer, setHoveredCustomer] = useState(null);
  const [showMoreReviews, setShowMoreReviews] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const reviewsContainerRef = useRef(null);
  
  // Chatbot states
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [awaitingLeadInfo, setAwaitingLeadInfo] = useState(false);
  const [leadStep, setLeadStep] = useState(0);
  const [userData, setUserData] = useState({ name: '', phone: '', city: '' });
  const [messageCount, setMessageCount] = useState(0);
  const [quickReplies, setQuickReplies] = useState([]);
  const [chatbotHover, setChatbotHover] = useState(false);

  // Initialize chat when opened
  useEffect(() => {
    if (isChatOpen && chatMessages.length === 0) {
      initChat();
    }
  }, [isChatOpen]);

  // Customer Reviews Data
  const customerReviews = [
    {
      id: 1,
      name: "Sangita Banait",
      location: "Amravati, Maharashtra",
      type: "Residential",
      rating: 5,
      review: "Installed solar panels last year and our electricity bills dropped by 85%! The installation was smooth and the team was professional.",
      image:  require('./images/customer1.jpeg'),
      savings: "₹7,300/month",
      systemSize: "5kW",
      tags: ["Residential", "5kW System", "Grid-tied"]
    },
    {
      id: 2,
      name: "Kunal Ruse",
      location: "Nagpur Industrial Area",
      type: "Commercial",
      rating: 5,
      review: "Our factory reduced energy costs by 68% with a 500kW solar system. The ROI was achieved in just 3.5 years.",
      image:  require('./images/customer2.jpeg'),
      savings: "₹2.2L/month",
      systemSize: "500kW",
      tags: ["Commercial", "500kW", "Industrial"]
    },
    {
      id: 3,
      name: "Dhanshree Nanotkar",
      location: "Nagpur",
      type: "Commercial",
      rating: 5,
      review: "As a tech company, sustainability is part of our brand. The solar installation was seamless, and we now generate 90% of our electricity needs.",
      image: require('./images/customer6.jpeg'),
      savings: "₹88,000/month",
      systemSize: "40kW",
      tags: ["Office", "Tech", "40kW"]
    }
  ];

  // More reviews for expandable section
  const moreReviews = [
    {
      id: 4,
      name: "Priya's Boutique Hotel",
      location: "Bhandara",
      type: "Commercial",
      rating: 4,
      review: "Solar power helped us become more sustainable and attract eco-conscious tourists. We save ₹45,000 monthly.",
      savings: "₹45,000/month",
      systemSize: "25kW",
      tags: ["Hotel", "Tourism", "25kW"]
    },
    {
      id: 5,
      name: "The Gupta Family Farm",
      location: "Yavatmal, Maharashtra",
      type: "Agricultural",
      rating: 5,
      review: "Solar water pumps transformed our farm. We now have reliable irrigation without worrying about electricity bills.",
      savings: "100% on fuel",
      systemSize: "10HP Solar Pump",
      tags: ["Agricultural", "Solar Pump", "Off-grid"]
    },
    {
      id: 6,
      name: "Green Valley School",
      location: "Akola",
      type: "Institutional",
      rating: 4,
      review: "We've reduced our carbon footprint and saved ₹60,000 monthly. Students learn about renewable energy firsthand.",
      savings: "₹60,000/month",
      systemSize: "35kW",
      tags: ["Educational", "School", "35kW"]
    }
  ];

  // Customer Stories (Featured)
  const customerStories = [
    {
      id: 1,
      title: "From Grid Dependency to Energy Independence",
      customer: "The Mehta Family",
      location: "Akola",
      duration: "2 years with solar",
      story: "We were paying ₹12,000 monthly for electricity. After installing a 8kW solar system with battery backup, we now pay only ₹800 for grid connection.",
      beforeImage: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=60",
      afterImage: "https://images.unsplash.com/photo-1542332213-9b5a5a3fad55?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80",
      savings: "₹1,34,400 saved in 2 years",
      system: "8kW with 10kWh battery",
      keyBenefit: "Complete energy independence"
    },
    {
      id: 2,
      title: "Scaling Business with Sustainable Energy",
      customer: "Precision Engineering Works",
      location: "Nagpur",
      duration: "18 months with solar",
      story: "Our manufacturing unit's electricity costs were limiting expansion. The 250kW solar installation freed up ₹18L annually for business growth.",
      beforeImage: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=60",
      afterImage: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80",
      savings: "₹27L saved to date",
      system: "250kW industrial system",
      keyBenefit: "Competitive advantage"
    }
  ];

  // Statistics Data
  const stats = [
    { number: "5000+", label: "Happy Customers" },
    { number: "98%", label: "Satisfaction Rate" },
    { number: "45MW+", label: "Solar Installed" },
    { number: "₹200Cr+", label: "Customer Savings" },
    { number: "10+", label: "Years Experience" },
    { number: "24/7", label: "Customer Support" }
  ];

  // Auto-scroll reviews
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveReview((prev) => (prev + 1) % customerReviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [customerReviews.length]);

  // Handle scroll for parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Chatbot functions
  const initChat = () => {
    addChatMessage("Hi 👋 Welcome to Blue Sky Energy! Our customers love us - 98% satisfaction rate! How can I help you?", 'bot');
    setTimeout(() => {
      addChatMessage("Choose an option to get started:", 'bot');
      setQuickReplies([
        "⭐ Customer Reviews", 
        "💰 See Savings", 
        "👥 Success Stories",
        "📞 Free Consultation"
      ]);
    }, 800);
  };

  const addChatMessage = (text, sender) => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const newMessage = {
      text,
      sender,
      time,
      id: Date.now()
    };
    setChatMessages(prev => [...prev, newMessage]);
    if (sender === 'user') {
      setMessageCount(prev => prev + 1);
    }
  };

  // Keyword Logic for Customers
  const matchKeywords = (input) => {
    const text = input.toLowerCase();
    if (text.includes('review') || text.includes('rating') || text.includes('feedback')) {
      return "⭐ Customer Reviews\n\n• 5,000+ Happy Customers\n• 98% Satisfaction Rate\n• Average Rating: 4.9/5\n\nFeatured Reviews:\nSangita Banait: \"85% bill reduction!\"\nKunal Ruse: \"68% energy cost savings!\"\nDhanshree Nanotkar: \"90% electricity from solar!\"\n\nWant to read more reviews?";
    }
    if (text.includes('save') || text.includes('saving') || text.includes('bill')) {
      return "💰 Customer Savings\n\n• Residential: 80-90% bill reduction\n• Commercial: 60-75% energy cost savings\n• Industrial: 50-70% operational cost savings\n\nExamples:\n• 5kW Home: Saves ₹7,300/month\n• 500kW Factory: Saves ₹2.2L/month\n• 40kW Office: Saves ₹88,000/month\n\nWant to calculate YOUR savings?";
    }
    if (text.includes('story') || text.includes('case') || text.includes('success')) {
      return "👥 Success Stories\n\n• Mehta Family: ₹1.34L saved in 2 years\n• Precision Engineering: ₹27L saved in 18 months\n• Priya's Hotel: ₹45k/month savings\n• Gupta Farm: 100% fuel savings\n• Green Valley School: ₹60k/month savings\n\nAll stories verified and documented!";
    }
    if (text.includes('consult') || text.includes('free') || text.includes('assessment')) {
      return "📞 Free Consultation\n\n• Free site assessment\n• Customized solar design\n• ROI calculation\n• Subsidy assistance\n• Financing options\n\n5000+ customers trust us! Want to schedule your free consultation?";
    }
    if (text.includes('residential') || text.includes('home') || text.includes('house')) {
      return "🏠 Residential Customers\n\n• 5kW System: Saves ₹7,300/month\n• 3kW System: Saves ₹4,500/month\n• 8kW System: Saves ₹12,000/month\n\nAverage Payback: 3-4 years\nThen FREE electricity for 20+ years!\n\nInterested in home solar?";
    }
    if (text.includes('commercial') || text.includes('business') || text.includes('office')) {
      return "🏢 Commercial Customers\n\n• 40kW Office: Saves ₹88,000/month\n• 25kW Hotel: Saves ₹45,000/month\n• 35kW School: Saves ₹60,000/month\n\nTax benefits + Accelerated depreciation\nROI: 3-5 years\n\nNeed commercial solar details?";
    }
    if (text.includes('industrial') || text.includes('factory') || text.includes('manufacturing')) {
      return "🏭 Industrial Customers\n\n• 500kW Factory: Saves ₹2.2L/month\n• 250kW Unit: Saves ₹1.5L/month\n• 100kW Plant: Saves ₹60k/month\n\nOperational cost reduction: 50-70%\nEnhanced competitiveness\n\nWant industrial solar information?";
    }
    if (text.includes('agriculture') || text.includes('farm') || text.includes('pump')) {
      return "🌾 Agricultural Customers\n\n• Solar Water Pumps: 100% fuel savings\n• 10HP System: Irrigates 5 acres\n• 5HP System: Irrigates 2 acres\n\nGovernment subsidies available\nReliable irrigation, day and night\n\nNeed agricultural solar details?";
    }
    return "I understand you're interested in our customer success stories! With 5,000+ happy customers and 98% satisfaction, I can help with reviews, savings calculations, success stories, or connect you with our happy customers.";
  };

  // Lead Capture Logic
  const handleLeadCapture = (input) => {
    if (leadStep === 0) {
      setUserData(prev => ({ ...prev, name: input }));
      setLeadStep(1);
      addChatMessage(`Nice to meet you, ${input}! 😊\n\nCould you please share your phone number?`, 'bot');
    } else if (leadStep === 1) {
      setUserData(prev => ({ ...prev, phone: input }));
      setLeadStep(2);
      addChatMessage("Great! And which city are you from?", 'bot');
    } else if (leadStep === 2) {
      setUserData(prev => ({ ...prev, city: input }));
      setLeadCaptured(true);
      setAwaitingLeadInfo(false);
      saveLead({ ...userData, city: input });
      addChatMessage(`Perfect! Thank you ${userData.name}! ✅\n\nJoin our 5000+ happy customers! Our team will contact you within 24 hours with a free consultation and savings estimate.`, 'bot');
    }
  };

  const saveLead = (data) => {
    const leadId = `lead_${Date.now()}`;
    localStorage.setItem(leadId, JSON.stringify({...data, timestamp: new Date()}));
    console.log("Customers Page Lead Saved:", data);
  };

  const handleChatSend = () => {
    const text = chatInput.trim();
    if (!text) return;

    addChatMessage(text, 'user');
    setChatInput('');
    setQuickReplies([]);

    setTimeout(() => {
      if (awaitingLeadInfo) {
        handleLeadCapture(text);
      } else {
        const response = matchKeywords(text);
        addChatMessage(response, 'bot');

        // Trigger Lead Capture after 2 interactions
        if (!leadCaptured && messageCount >= 1) {
          setTimeout(() => {
            addChatMessage("Join our happy customer family! 📋\n\nCan I get your name to assist you better?", 'bot');
            setAwaitingLeadInfo(true);
          }, 1500);
        }
      }
    }, 600);
  };

  const handleQuickReply = (option) => {
    setChatInput(option);
    setTimeout(() => {
      handleChatSend();
    }, 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleChatSend();
    }
  };

  const styles = {
    container: {
      fontFamily: "Cambria, serif",
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      color: '#123249',
      overflowX: 'hidden',
      position: 'relative'
    },
    
    // Chatbot Styles
    chatbotContainer: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: '9999',
      fontFamily: "Cambria, serif"
    },
    chatToggleBtn: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #447793, #2D5A74)',
      color: 'white',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      boxShadow: '0 8px 30px rgba(68, 119, 147, 0.3)',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '10000',
      padding: '0',
      animation: chatbotHover ? 'pulse 0.5s infinite' : 'pulse 2s infinite'
    },
    chatWindow: {
      position: 'fixed',
      bottom: '90px',
      right: '20px',
      width: '380px',
      maxWidth: 'calc(100vw - 40px)',
      height: '550px',
      maxHeight: '70vh',
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 10px 40px rgba(6, 18, 33, 0.15)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      zIndex: '9999',
      transition: 'all 0.3s ease',
      opacity: isChatOpen ? '1' : '0',
      visibility: isChatOpen ? 'visible' : 'hidden',
      transform: isChatOpen ? 'translateY(0)' : 'translateY(20px)',
      border: '1px solid #DDE8ED'
    },
    chatHeader: {
      background: 'linear-gradient(135deg, #447793, #2D5A74)',
      color: 'white',
      padding: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    headerIcon: {
      background: 'rgba(255, 255, 255, 0.2)',
      padding: '10px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      fontSize: '18px'
    },
    chatHeaderTitle: {
      margin: '0',
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '1.2',
      color: 'white'
    },
    chatHeaderSubtitle: {
      margin: '0',
      fontSize: '12px',
      opacity: '0.9',
      lineHeight: '1.2',
      color: '#DDE8ED'
    },
    closeChatBtn: {
      background: 'none',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
      fontSize: '18px',
      padding: '5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'opacity 0.2s'
    },
    quickReplies: {
      padding: '12px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      background: '#DDE8ED',
      borderBottom: '1px solid #DDE8ED'
    },
    replyBtn: {
      background: 'white',
      border: '1px solid #447793',
      color: '#123249',
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: '13px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap',
      fontWeight: '500',
      fontFamily: "Cambria, serif"
    },
    chatMessages: {
      flex: '1',
      padding: '16px',
      background: '#DDE8ED',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    message: {
      maxWidth: '80%',
      padding: '12px 16px',
      borderRadius: '18px',
      fontSize: '14px',
      lineHeight: '1.5',
      whiteSpace: 'pre-line',
      wordWrap: 'break-word',
      position: 'relative',
      animation: 'fadeIn 0.3s ease'
    },
    botMessage: {
      background: 'white',
      alignSelf: 'flex-start',
      borderBottomLeftRadius: '4px',
      boxShadow: '0 2px 8px rgba(6, 18, 33, 0.08)',
      color: '#123249',
      border: '1px solid #DDE8ED'
    },
    userMessage: {
      background: 'linear-gradient(135deg, #447793, #2D5A74)',
      color: 'white',
      alignSelf: 'flex-end',
      borderBottomRightRadius: '4px'
    },
    messageTime: {
      fontSize: '11px',
      marginTop: '6px',
      display: 'block',
      opacity: '0.7'
    },
    chatInputArea: {
      padding: '16px',
      borderTop: '1px solid #DDE8ED',
      background: 'white'
    },
    inputWrapper: {
      display: 'flex',
      gap: '10px',
      marginBottom: '8px'
    },
    chatInput: {
      flex: '1',
      padding: '12px 16px',
      border: '1px solid #DDE8ED',
      borderRadius: '25px',
      outline: 'none',
      fontSize: '14px',
      fontFamily: "Cambria, serif",
      transition: 'border-color 0.2s',
      color: '#123249'
    },
    sendBtn: {
      background: 'linear-gradient(135deg, #447793, #2D5A74)',
      color: 'white',
      border: 'none',
      width: '45px',
      height: '45px',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      transition: 'all 0.2s ease',
      padding: '0'
    },
    footerText: {
      textAlign: 'center',
      fontSize: '11px',
      color: '#2D5A74',
      margin: '0'
    },
    
    heroSection: {
      background: `linear-gradient(rgba(18, 50, 73, 0.95), rgba(45, 90, 116, 0.95)), url('https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1600&q=80')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      color: 'white',
      padding: '60px 20px 40px',
      textAlign: 'center',
      position: 'relative'
    },
    heroTitle: {
      fontSize: '2.2rem',
      fontWeight: '800',
      marginBottom: '15px',
      textShadow: '2px 2px 4px rgba(6, 18, 33, 0.3)',
      color: 'white'
    },
    heroSubtitle: {
      fontSize: '1rem',
      maxWidth: '800px',
      margin: '0 auto 30px',
      opacity: '0.95',
      lineHeight: '1.6',
      color: '#DDE8ED'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '15px',
      marginTop: '30px',
      maxWidth: '1000px',
      margin: '30px auto 0'
    },
    statItem: {
      backgroundColor: 'rgba(68, 119, 147, 0.15)',
      backdropFilter: 'blur(10px)',
      padding: '15px 10px',
      borderRadius: '12px',
      border: '1px solid rgba(221, 232, 237, 0.3)',
      transition: 'all 0.3s ease',
      ':hover': {
        backgroundColor: 'rgba(68, 119, 147, 0.25)',
        transform: 'translateY(-5px)'
      }
    },
    statNumber: {
      fontSize: '1.5rem',
      fontWeight: '800',
      marginBottom: '5px',
      color: '#DDE8ED'
    },
    statLabel: {
      fontSize: '0.8rem',
      opacity: '0.9',
      fontWeight: '600',
      color: 'white'
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '30px 20px'
    },
    sectionHeader: {
      textAlign: 'center',
      marginBottom: '30px'
    },
    sectionTitle: {
      fontSize: '1.8rem',
      fontWeight: '800',
      color: '#123249',
      marginBottom: '12px',
      position: 'relative',
      display: 'inline-block',
      paddingBottom: '10px'
    },
    sectionTitleAfter: {
      content: '""',
      position: 'absolute',
      bottom: '0',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '80px',
      height: '3px',
      background: 'linear-gradient(90deg, #447793, #2D5A74)',
      borderRadius: '2px'
    },
    sectionSubtitle: {
      fontSize: '0.95rem',
      color: '#2D5A74',
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: '1.6'
    },
    featuredReviews: {
      padding: '40px 20px',
      backgroundColor: '#DDE8ED'
    },
    reviewSlider: {
      maxWidth: '800px',
      margin: '20px auto 0',
      position: 'relative'
    },
    reviewCard: {
      backgroundColor: 'white',
      borderRadius: '15px',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(6, 18, 33, 0.08)',
      padding: '25px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      transition: 'all 0.4s ease',
      minHeight: '280px',
      border: '1px solid #DDE8ED'
    },
    customerInfo: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: '15px'
    },
    customerAvatar: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      objectFit: 'cover',
      border: '3px solid #447793',
      marginBottom: '12px'
    },
    customerName: {
      fontSize: '1.1rem',
      fontWeight: '700',
      color: '#123249',
      marginBottom: '5px'
    },
    customerLocation: {
      color: '#2D5A74',
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      marginBottom: '8px'
    },
    customerType: {
      display: 'inline-block',
      backgroundColor: '#DDE8ED',
      color: '#123249',
      padding: '3px 10px',
      borderRadius: '15px',
      fontSize: '0.75rem',
      fontWeight: '600'
    },
    ratingStars: {
      color: '#447793',
      fontSize: '1rem',
      marginBottom: '12px'
    },
    reviewText: {
      color: '#2D5A74',
      fontSize: '0.9rem',
      lineHeight: '1.6',
      marginBottom: '15px',
      fontStyle: 'italic',
      maxWidth: '600px'
    },
    savingsBadge: {
      backgroundColor: '#DDE8ED',
      color: '#123249',
      padding: '6px 16px',
      borderRadius: '20px',
      fontWeight: '700',
      fontSize: '0.85rem',
      border: '1px solid #447793',
      marginTop: '8px'
    },
    sliderNav: {
      display: 'flex',
      justifyContent: 'center',
      gap: '10px',
      marginTop: '20px'
    },
    navDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#DDE8ED',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    activeNavDot: {
      backgroundColor: '#447793',
      transform: 'scale(1.3)'
    },
    storiesSection: {
      padding: '40px 20px',
      backgroundColor: '#ffffff'
    },
    storiesGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '25px',
      marginTop: '25px'
    },
    storyCard: {
      backgroundColor: 'white',
      borderRadius: '15px',
      overflow: 'hidden',
      boxShadow: '0 8px 25px rgba(6, 18, 33, 0.06)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '1px solid #DDE8ED',
      ':hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 12px 30px rgba(68, 119, 147, 0.1)'
      }
    },
    storyHeader: {
      padding: '20px 20px 12px',
      borderBottom: '2px solid #DDE8ED'
    },
    storyTitle: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: '#123249',
      marginBottom: '8px'
    },
    storyCustomer: {
      color: '#447793',
      fontWeight: '600',
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap'
    },
    storyContent: {
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    storyImageContainer: {
      position: 'relative',
      borderRadius: '10px',
      overflow: 'hidden',
      height: '200px'
    },
    storyImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.5s ease'
    },
    storyText: {
      color: '#2D5A74',
      fontSize: '0.9rem',
      lineHeight: '1.6',
      marginBottom: '15px'
    },
    storyStats: {
      backgroundColor: '#DDE8ED',
      padding: '12px',
      borderRadius: '10px',
      marginTop: '12px'
    },
    statItemSmall: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '6px',
      fontSize: '0.8rem'
    },
    statLabelSmall: {
      color: '#2D5A74'
    },
    statValueSmall: {
      fontWeight: '600',
      color: '#123249'
    },
    allReviewsSection: {
      padding: '40px 20px',
      backgroundColor: '#DDE8ED'
    },
    reviewsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '20px',
      marginTop: '25px'
    },
    reviewCardSmall: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 8px 20px rgba(6, 18, 33, 0.04)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '2px solid transparent',
      ':hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 10px 25px rgba(68, 119, 147, 0.1)',
        borderColor: '#447793'
      }
    },
    reviewHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px'
    },
    reviewCustomer: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    smallAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      objectFit: 'cover',
      border: '2px solid #447793'
    },
    smallCustomerName: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#123249',
      marginBottom: '3px'
    },
    smallCustomerType: {
      color: '#2D5A74',
      fontSize: '0.75rem'
    },
    smallRating: {
      color: '#447793',
      fontSize: '0.85rem'
    },
    smallReviewText: {
      color: '#2D5A74',
      fontSize: '0.85rem',
      lineHeight: '1.6',
      marginBottom: '12px',
      height: '65px',
      overflow: 'hidden'
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '5px',
      marginTop: '10px'
    },
    tag: {
      backgroundColor: '#DDE8ED',
      color: '#123249',
      padding: '2px 8px',
      borderRadius: '8px',
      fontSize: '0.65rem',
      fontWeight: '500'
    },
    showMoreButton: {
      display: 'block',
      width: '160px',
      margin: '25px auto 0',
      background: 'linear-gradient(135deg, #447793, #2D5A74)',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '25px',
      fontWeight: '600',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 15px rgba(68, 119, 147, 0.3)'
      }
    },
    processSection: {
      padding: '40px 20px',
      backgroundColor: '#ffffff'
    },
    processContainer: {
      maxWidth: '900px',
      margin: '0 auto'
    },
    processSteps: {
      display: 'flex',
      flexDirection: 'column',
      gap: '25px',
      marginTop: '30px',
      position: 'relative'
    },
    processStep: {
      textAlign: 'center',
      padding: '0 15px',
      position: 'relative'
    },
    stepNumber: {
      width: '45px',
      height: '45px',
      background: 'linear-gradient(135deg, #447793, #2D5A74)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 12px',
      fontSize: '1.1rem',
      color: 'white',
      fontWeight: '700',
      boxShadow: '0 6px 15px rgba(68, 119, 147, 0.3)'
    },
    stepTitle: {
      fontSize: '1rem',
      fontWeight: '700',
      color: '#123249',
      marginBottom: '6px'
    },
    stepDescription: {
      color: '#2D5A74',
      fontSize: '0.85rem',
      lineHeight: '1.5'
    },
    connectingLine: {
      display: 'none'
    },
  };

  const allReviews = [...customerReviews, ...(showMoreReviews ? moreReviews : [])];

  return (
    <div style={styles.container}>
      {/* Responsive CSS */}
      <style>
        {`
          /* Color Palette */
          :root {
            --color-deep-teal: #061221;
            --color-dark-navy: #123249;
            --color-midnight: #2D5A74;
            --color-slate: #447793;
            --color-frost: #DDE8ED;
            --color-white: #ffffff;
            --gradient-primary: linear-gradient(135deg, #447793 0%, #2D5A74 100%);
            --gradient-hover: linear-gradient(135deg, #2D5A74 0%, #123249 100%);
            --shadow-sm: 0 2px 15px rgba(6, 18, 33, 0.08);
            --shadow-md: 0 4px 20px rgba(6, 18, 33, 0.12);
            --shadow-lg: 0 10px 30px rgba(6, 18, 33, 0.15);
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          /* Mobile Styles */
          @media (max-width: 768px) {
            .stats-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            
            .hero-title {
              font-size: 1.8rem !important;
            }
            
            .hero-subtitle {
              font-size: 0.9rem !important;
              padding: 0 10px;
            }
            
            .section-title {
              font-size: 1.5rem !important;
            }
            
            .review-card {
              padding: 20px 15px !important;
              min-height: 250px !important;
            }
            
            .customer-avatar {
              width: 55px !important;
              height: 55px !important;
            }
            
            .chat-window {
              width: calc(100vw - 40px) !important;
              height: 500px !important;
              bottom: 80px !important;
              right: 20px !important;
              max-height: 65vh !important;
            }
            
            .chat-toggle-btn {
              width: 55px !important;
              height: 55px !important;
              font-size: 20px !important;
            }
            
            .story-content {
              grid-template-columns: 1fr !important;
            }
            
            .story-image-container {
              height: 180px !important;
            }
            
            .reviews-grid {
              grid-template-columns: 1fr !important;
            }
            
            .process-steps {
              flex-direction: column !important;
            }
            
            .connecting-line {
              display: none !important;
            }
            
            .step-number {
              width: 40px !important;
              height: 40px !important;
              font-size: 1rem !important;
            }
            
            .chat-message {
              max-width: 90% !important;
              font-size: 13px !important;
            }
          }
          
          @media (max-width: 480px) {
            .stats-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
            }
            
            .stat-item {
              padding: 12px 8px !important;
            }
            
            .stat-number {
              font-size: 1.3rem !important;
            }
            
            .stat-label {
              font-size: 0.7rem !important;
            }
            
            .hero-title {
              font-size: 1.5rem !important;
              margin-bottom: 12px !important;
            }
            
            .hero-section {
              padding: 40px 15px 30px !important;
            }
            
            .section-title {
              font-size: 1.3rem !important;
            }
            
            .section-subtitle {
              font-size: 0.85rem !important;
            }
            
            .review-card {
              padding: 15px !important;
              min-height: 220px !important;
            }
            
            .review-text {
              font-size: 0.85rem !important;
              margin-bottom: 12px !important;
            }
            
            .customer-name {
              font-size: 1rem !important;
            }
            
            .savings-badge {
              font-size: 0.8rem !important;
              padding: 5px 12px !important;
            }
            
            .chat-window {
              width: calc(100vw - 20px) !important;
              right: 10px !important;
              left: 10px !important;
              height: 450px !important;
              max-height: 60vh !important;
            }
            
            .reply-btn {
              font-size: 11px !important;
              padding: 6px 10px !important;
            }
            
            .chat-input {
              padding: 10px 14px !important;
              font-size: 13px !important;
            }
            
            .chat-header-title {
              font-size: 14px !important;
            }
            
            .chat-header-subtitle {
              font-size: 11px !important;
            }
            
            .chat-toggle-btn {
              width: 50px !important;
              height: 50px !important;
              font-size: 18px !important;
              bottom: 15px !important;
              right: 15px !important;
            }
            
            .show-more-button {
              width: 140px !important;
              padding: 8px 16px !important;
              font-size: 0.85rem !important;
            }
            
            .story-title {
              font-size: 1.1rem !important;
            }
            
            .story-customer {
              font-size: 0.8rem !important;
            }
            
            .story-image-container {
              height: 160px !important;
            }
            
            .small-review-text {
              height: 60px !important;
              font-size: 0.8rem !important;
            }
            
            .tag {
              font-size: 0.6rem !important;
              padding: 2px 6px !important;
            }
          }
          
          @media (min-width: 769px) and (max-width: 1024px) {
            .stats-grid {
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 20px !important;
            }
            
            .stories-grid {
              grid-template-columns: 1fr !important;
            }
            
            .reviews-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            
            .process-steps {
              flex-wrap: wrap !important;
              flex-direction: row !important;
            }
            
            .process-step {
              flex: 0 0 calc(33.333% - 20px) !important;
              margin-bottom: 30px !important;
            }
            
            .chat-window {
              width: 350px !important;
            }
          }
          
          @media (min-width: 1025px) {
            .stats-grid {
              grid-template-columns: repeat(6, 1fr) !important;
            }
            
            .stories-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            
            .reviews-grid {
              grid-template-columns: repeat(3, 1fr) !important;
            }
            
            .process-steps {
              flex-direction: row !important;
            }
            
            .connecting-line {
              display: block !important;
            }
            
            .story-content {
              grid-template-columns: 1fr 1fr !important;
            }
          }
          
          /* Tablet Styles */
          @media (min-width: 481px) and (max-width: 768px) {
            .stories-grid {
              grid-template-columns: 1fr !important;
            }
            
            .reviews-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            
            .process-steps {
              flex-direction: column !important;
            }
            
            .stat-number {
              font-size: 1.7rem !important;
            }
          }
          
          /* Section Title Underline */
          .section-title {
            position: relative;
          }
          
          .section-title::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 80px;
            height: 3px;
            background: linear-gradient(90deg, #447793, #2D5A74);
            border-radius: 2px;
          }
          
          /* Hover Effects */
          .stat-item:hover {
            background-color: rgba(68, 119, 147, 0.25) !important;
            transform: translateY(-5px) !important;
          }
          
          .story-card:hover {
            transform: translateY(-5px) !important;
            box-shadow: 0 12px 30px rgba(68, 119, 147, 0.1) !important;
          }
          
          .review-card-small:hover {
            transform: translateY(-3px) !important;
            box-shadow: 0 10px 25px rgba(68, 119, 147, 0.1) !important;
            border-color: #447793 !important;
          }
          
          .show-more-button:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 15px rgba(68, 119, 147, 0.3) !important;
            background: linear-gradient(135deg, #2D5A74, #123249) !important;
          }
        `}
      </style>

      {/* Hero Section - Blue Theme */}
      <section style={styles.heroSection}>
        <h1 style={styles.heroTitle} className="hero-title">OUR HAPPY CUSTOMERS</h1>
        <p style={styles.heroSubtitle} className="hero-subtitle">
          Join 5,000+ satisfied homes and businesses who have made the switch to clean, 
          affordable Blue Sky Energy. See real savings, real results, and real stories.
        </p>
        
        <div style={styles.statsGrid} className="stats-grid">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              style={styles.statItem}
              className="stat-item"
              onMouseEnter={() => setHoveredCustomer(`stat-${index}`)}
              onMouseLeave={() => setHoveredCustomer(null)}
            >
              <div style={styles.statNumber} className="stat-number">{stat.number}</div>
              <div style={styles.statLabel} className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Review Slider - Blue Theme */}
      <section style={styles.featuredReviews}>
        <div style={styles.mainContent}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle} className="section-title">FEATURED CUSTOMER REVIEWS</h2>
            <p style={styles.sectionSubtitle} className="section-subtitle">
              Real stories from real customers who have transformed their energy consumption 
              and achieved significant savings with our solar solutions.
            </p>
          </div>
          
          <div style={styles.reviewSlider}>
            <div style={styles.reviewCard} className="review-card">
              <div style={styles.customerInfo}>
                <img 
                  src={customerReviews[activeReview].image} 
                  alt={customerReviews[activeReview].name}
                  style={{
                    ...styles.customerAvatar,
                    transform: hoveredCustomer === `featured-${activeReview}` ? 'scale(1.1)' : 'scale(1)'
                  }}
                  className="customer-avatar"
                  onMouseEnter={() => setHoveredCustomer(`featured-${activeReview}`)}
                  onMouseLeave={() => setHoveredCustomer(null)}
                />
                <h3 style={styles.customerName} className="customer-name">{customerReviews[activeReview].name}</h3>
                <div style={styles.customerLocation} className="customer-location">
                  📍 {customerReviews[activeReview].location}
                </div>
                <span style={styles.customerType} className="customer-type">{customerReviews[activeReview].type}</span>
              </div>
              
              <div style={styles.ratingStars} className="rating-stars">
                {'★'.repeat(customerReviews[activeReview].rating)}
              </div>
              
              <p style={styles.reviewText} className="review-text">"{customerReviews[activeReview].review}"</p>
              
              <div style={styles.savingsBadge} className="savings-badge">
                💰 Saves {customerReviews[activeReview].savings} • ⚡ {customerReviews[activeReview].systemSize}
              </div>
            </div>
            
            <div style={styles.sliderNav}>
              {customerReviews.map((_, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.navDot,
                    ...(index === activeReview ? styles.activeNavDot : {})
                  }}
                  onClick={() => setActiveReview(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Stories - Blue Theme */}
      <section style={styles.storiesSection}>
        <div style={styles.mainContent}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle} className="section-title">SUCCESS STORIES</h2>
            <p style={styles.sectionSubtitle} className="section-subtitle">
              In-depth case studies showing the transformative impact of solar energy 
              on homes and businesses across India.
            </p>
          </div>
          
          <div style={styles.storiesGrid} className="stories-grid">
            {customerStories.map((story, index) => (
              <div 
                key={story.id} 
                style={styles.storyCard}
                className="story-card"
                onMouseEnter={() => setHoveredCustomer(`story-${story.id}`)}
                onMouseLeave={() => setHoveredCustomer(null)}
              >
                <div style={styles.storyHeader}>
                  <h3 style={styles.storyTitle} className="story-title">{story.title}</h3>
                  <div style={styles.storyCustomer} className="story-customer">
                    👤 {story.customer} • 📍 {story.location} • ⏳ {story.duration}
                  </div>
                </div>
                
                <div style={{
                  ...styles.storyContent,
                  display: 'grid',
                  gridTemplateColumns: '1fr'
                }} className="story-content">
                  <div style={styles.storyImageContainer} className="story-image-container">
                    <img 
                      src={index % 2 === 0 ? story.beforeImage : story.afterImage} 
                      alt={index % 2 === 0 ? "Before Solar" : "After Solar"}
                      style={{
                        ...styles.storyImage,
                        transform: hoveredCustomer === `story-${story.id}` ? 'scale(1.1)' : 'scale(1)'
                      }}
                      className="story-image"
                    />
                  </div>
                  <div>
                    <p style={styles.storyText} className="story-text">{story.story}</p>
                    <div style={styles.storyStats} className="story-stats">
                      <div style={styles.statItemSmall} className="stat-item-small">
                        <span style={styles.statLabelSmall} className="stat-label-small">Total Savings:</span>
                        <span style={styles.statValueSmall} className="stat-value-small">{story.savings}</span>
                      </div>
                      <div style={styles.statItemSmall} className="stat-item-small">
                        <span style={styles.statLabelSmall} className="stat-label-small">System Size:</span>
                        <span style={styles.statValueSmall} className="stat-value-small">{story.system}</span>
                      </div>
                      <div style={styles.statItemSmall} className="stat-item-small">
                        <span style={styles.statLabelSmall} className="stat-label-small">Key Benefit:</span>
                        <span style={{...styles.statValueSmall, color: '#447793'}} className="stat-value-small">
                          {story.keyBenefit}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section - Blue Theme */}
      <section style={styles.processSection}>
        <div style={styles.processContainer}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle} className="section-title">OUR CUSTOMER JOURNEY</h2>
            <p style={styles.sectionSubtitle} className="section-subtitle">
              From initial inquiry to solar activation, we ensure a seamless experience 
              for every customer.
            </p>
          </div>
          
          <div style={styles.processSteps} className="process-steps">
            <div style={styles.connectingLine} className="connecting-line"></div>
            {[
              { number: '01', title: 'Consultation', desc: 'Free energy assessment & custom quote' },
              { number: '02', title: 'Design', desc: 'System design & approvals' },
              { number: '03', title: 'Installation', desc: 'Professional installation by experts' },
              { number: '04', title: 'Activation', desc: 'System activation & training' },
              { number: '05', title: 'Support', desc: '24/7 monitoring & maintenance' }
            ].map((step, index) => (
              <div key={index} style={styles.processStep} className="process-step">
                <div style={styles.stepNumber} className="step-number">{step.number}</div>
                <h3 style={styles.stepTitle} className="step-title">{step.title}</h3>
                <p style={styles.stepDescription} className="step-description">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Reviews Grid - Blue Theme */}
      <section style={styles.allReviewsSection}>
        <div style={styles.mainContent}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle} className="section-title">MORE CUSTOMER REVIEWS</h2>
            <p style={styles.sectionSubtitle} className="section-subtitle">
              Browse through hundreds of verified customer reviews. 
              See what others are saying about their solar experience.
            </p>
          </div>
          
          <div style={styles.reviewsGrid} className="reviews-grid">
            {allReviews.map((review) => (
              <div 
                key={review.id} 
                style={styles.reviewCardSmall}
                className="review-card-small"
                onMouseEnter={() => setHoveredCustomer(`review-${review.id}`)}
                onMouseLeave={() => setHoveredCustomer(null)}
              >
                <div style={styles.reviewHeader}>
                  <div style={styles.reviewCustomer}>
                    <img 
                      src={review.image || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80'} 
                      alt={review.name}
                      style={styles.smallAvatar}
                      className="small-avatar"
                    />
                    <div>
                      <h4 style={styles.smallCustomerName} className="small-customer-name">{review.name}</h4>
                      <div style={styles.smallCustomerType} className="small-customer-type">
                        {review.type} • {review.location}
                      </div>
                    </div>
                  </div>
                  <div style={styles.smallRating} className="small-rating">
                    {'★'.repeat(review.rating)}
                  </div>
                </div>
                
                <p style={styles.smallReviewText} className="small-review-text">"{review.review}"</p>
                
                <div style={styles.tagsContainer} className="tags-container">
                  {review.tags.map((tag, index) => (
                    <span key={index} style={styles.tag} className="tag">{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {!showMoreReviews && (
            <button 
              style={styles.showMoreButton}
              className="show-more-button"
              onClick={() => setShowMoreReviews(true)}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #2D5A74, #123249)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 15px rgba(68, 119, 147, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #447793, #2D5A74)';
                e.target.style.transform = 'none';
                e.target.style.boxShadow = 'none';
              }}
            >
              Load More Reviews ↓
            </button>
          )}
        </div>
      </section>

      {/* Chatbot Component - Blue Theme */}
      <div style={styles.chatbotContainer}>
        {/* Chat Toggle Button */}
        <button 
          style={{
            ...styles.chatToggleBtn,
            animation: chatbotHover ? 'pulse 0.5s infinite' : 'pulse 2s infinite'
          }}
          className="chat-toggle-btn"
          onClick={() => setIsChatOpen(!isChatOpen)}
          onMouseEnter={() => setChatbotHover(true)}
          onMouseLeave={() => setChatbotHover(false)}
        >
          💬
        </button>

        {/* Chat Window */}
        <div style={styles.chatWindow} className="chat-window">
          {/* Chat Header */}
          <div style={styles.chatHeader}>
            <div style={styles.headerInfo}>
              <div style={styles.headerIcon}>
                ⭐
              </div>
              <div>
                <h3 style={styles.chatHeaderTitle} className="chat-header-title">Blue Sky Energy</h3>
                <p style={styles.chatHeaderSubtitle} className="chat-header-subtitle">98% Customer Satisfaction</p>
              </div>
            </div>
            <button 
              style={styles.closeChatBtn}
              onClick={() => setIsChatOpen(false)}
            >
              ✕
            </button>
          </div>

          {/* Quick Replies */}
          {quickReplies.length > 0 && (
            <div style={styles.quickReplies}>
              {quickReplies.map((option, index) => (
                <button
                  key={index}
                  style={styles.replyBtn}
                  className="reply-btn"
                  onClick={() => handleQuickReply(option)}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#447793';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = '#123249';
                    e.target.style.transform = 'none';
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {/* Chat Messages */}
          <div style={styles.chatMessages}>
            {chatMessages.map((msg) => (
              <div 
                key={msg.id} 
                style={{
                  ...styles.message,
                  ...(msg.sender === 'bot' ? styles.botMessage : styles.userMessage)
                }}
                className="chat-message"
              >
                {msg.text}
                <span style={styles.messageTime}>{msg.time}</span>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div style={styles.chatInputArea}>
            <div style={styles.inputWrapper}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                style={styles.chatInput}
                className="chat-input"
                onFocus={(e) => e.target.style.borderColor = '#447793'}
                onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
              />
              <button 
                style={styles.sendBtn}
                onClick={handleChatSend}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #2D5A74, #123249)';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #447793, #2D5A74)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                📤
              </button>
            </div>
            <p style={styles.footerText}>5000+ Happy Customers Trust Us</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;